import React, { useState, useEffect, useMemo, useRef } from "react";
import noidaImg from "../assets/Noida.png";
import firstImg from "../assets/1img.png"
import secondImg from "../assets/2img.png"
import thirdImg from "../assets/3img.png"
import fourthImg from "../assets/4img.png"
import fifthImg from "../assets/5img.png"
import sixthImg from "../assets/6img.png"
import seventhImg from "../assets/7img.png"
import logoImg from "../assets/Logo.jpeg";
import { Link } from "react-router-dom";
import '../styles/GreaterNoidaInfo.css';
import Seo from './Seo';
import EnquiryModal from './EnquiryModal';
import LocationMap from './LocationMap';
import locations, { locationSchema } from '../data/locations';

export default function GreaterNoidaInfo() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [allImagesPreloaded, setAllImagesPreloaded] = useState(false);
  const datesSectionRef = useRef(null);
  const privateExperienceSectionRef = useRef(null);

  const [enquiryOpen, setEnquiryOpen] = useState(false);

  const location = locations.greaterNoida;
  const greaterNoidaSchema = locationSchema(location);

  const announceEnquiryUrl = `https://wa.me/918279845322?text=${encodeURIComponent(
    "Hi Mystery Mansion, I'd like to be notified when the next Greater Noida mystery dates are announced."
  )}`;

  const scrollToDates = () => {
    datesSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToPrivateExperience = () => {
    privateExperienceSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const navItems = [
    { name: "Home", href: "/" },
    { name: "The Game", href: "/#about-game" },
    { name: "Locations", href: "/#locations" },
    { name: "FAQs", href: "/#faqs" },
    { name: "Blog", href: "/blog" },
    { name: "The Architect", href: "/creator" }
  ];
  const propertyImages = useMemo(() => [
    firstImg,
    secondImg,
    thirdImg,
    fourthImg,
    fifthImg,
    sixthImg,
    seventhImg
  ], []);

  useEffect(() => {
    const preloadImages = async () => {
      const imagePromises = propertyImages.map((src, index) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(src);
          img.onerror = reject;
          img.src = src;
        });
      });

      try {
        await Promise.all(imagePromises);
        setAllImagesPreloaded(true);
      } catch (error) {
        console.error('Error preloading images:', error);
        setAllImagesPreloaded(true);
      }
    };

    preloadImages();
  }, [propertyImages]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('nav')) setIsMenuOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  const changeImage = (newIndex) => {
    if (newIndex !== currentImageIndex) setCurrentImageIndex(newIndex);
  };
  const nextImage = () => changeImage(currentImageIndex === propertyImages.length - 1 ? 0 : currentImageIndex + 1);
  const prevImage = () => changeImage(currentImageIndex === 0 ? propertyImages.length - 1 : currentImageIndex - 1);

  return (
    <div className="page-greater-noida">
      <EnquiryModal
        open={enquiryOpen}
        heading="Enquire about upcoming dates?"
        body="We'll open WhatsApp with a message ready to send, and the organiser will let you know as soon as the next Greater Noida dates are announced."
        waUrl={announceEnquiryUrl}
        onClose={() => setEnquiryOpen(false)}
      />

      <Seo
        path="/greater-noida"
        title="Things to Do in Greater Noida | Mystery Mansion"
        description="Looking for unique things to do in Greater Noida? Experience an immersive real-life mystery game with your group during a private villa staycation."
      >
        {greaterNoidaSchema && (
          <script type="application/ld+json">{JSON.stringify(greaterNoidaSchema)}</script>
        )}
      </Seo>


      {/* Navigation */}
      <nav className="gn-nav">
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '64px' }}>
            <Link to="/"><img src={logoImg} alt="Mystery Mansion Logo" style={{ height: '56px', width: 'auto', filter: 'drop-shadow(0 0 20px rgba(201,168,76,0.1))' }} /></Link>
            <div className="hidden md:block">
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {navItems.map((item, i) => item.href.startsWith('/#') || item.href.startsWith('#')
                  ? <a key={i} href={item.href} className="gn-nav-link">{item.name}</a>
                  : <Link key={i} to={item.href} className="gn-nav-link">{item.name}</Link>
                )}
              </div>
            </div>
            <div className="md:hidden">
              <button onClick={(e) => { e.stopPropagation(); setIsMenuOpen(!isMenuOpen); }} style={{ color: 'var(--text-secondary)', padding: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>
                <svg style={{ width: '24px', height: '24px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isMenuOpen ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
                </svg>
              </button>
            </div>
          </div>
          <div className={`md:hidden gn-mobile-menu ${isMenuOpen ? 'open' : ''}`}>
            {navItems.map((item, i) => item.href.startsWith('/#') || item.href.startsWith('#')
              ? <a key={i} href={item.href} className="gn-nav-link" onClick={() => setIsMenuOpen(false)}>{item.name}</a>
              : <Link key={i} to={item.href} className="gn-nav-link" onClick={() => setIsMenuOpen(false)}>{item.name}</Link>
            )}
          </div>
        </div>
      </nav>

      {/* Hero / Property Info */}
      <section className="gn-hero">
        {/* Property details + image */}
        <div className="gn-content-wrap">
          <div className="gn-text">
            <h1 className="font-bold gn-title">
              {"About The Property (Greater Noida)".split('').map((letter, index) => (
                <span key={index} className="wavy-letter">{letter === ' ' ? '\u00A0' : letter}</span>
              ))}
            </h1>
            <div className="gn-list">
              <div>Farmhouse with elegant interiors—perfect for mystery and intrigue</div>
              <div>4 bedrooms and 4+1 bathrooms for comfortable group stays</div>
              <div>Lush outdoor space and garden area to explore</div>
              <div>Pristine swimming pool to add a 24x7 mystery deep-dive</div>
              <div>Ideal for 8–15 guests, where camaraderie and secrets intertwine</div>
              <div>Check-in from 5pm, check-out by 10am</div>
            </div>
          </div>
          <div className="gn-img-side">
            {!allImagesPreloaded ? (
              <div className="gn-carousel-container">
                <div className="gn-loading-spinner"><div className="gn-spinner"></div></div>
              </div>
            ) : (
              <div className="gn-carousel-container">
                <button onClick={prevImage} className="gn-carousel-arrow prev" aria-label="Previous image"><svg className="carousel-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg></button>
                <div className="gn-carousel-track" style={{ '--slide-count': propertyImages.length, '--current-index': currentImageIndex }}>
                  {propertyImages.map((image, index) => (
                    <div key={index} className="gn-carousel-slide">
                      <img src={image} alt={`Greater Noida property view ${index + 1}`} loading={index < 3 ? "eager" : "lazy"} />
                    </div>
                  ))}
                </div>
                <button onClick={nextImage} className="gn-carousel-arrow next" aria-label="Next image"><svg className="carousel-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg></button>
                <div className="gn-image-counter">{currentImageIndex + 1} / {propertyImages.length}</div>
              </div>
            )}
          </div>
        </div>

        {/* Booking CTA */}
        <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', paddingTop: '20px', paddingBottom: '60px', paddingLeft: '1rem', paddingRight: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap', maxWidth: '900px', padding: '0 1rem' }}>
            <button className="gn-cta-btn" onClick={scrollToDates}>Join a Scheduled Mystery</button>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', fontFamily: 'var(--font-body)', lineHeight: '1.6', margin: 0, maxWidth: '400px', textAlign: 'center' }}>
              Book seats for our upcoming mystery events on pre-announced dates and solve the case with other guests.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '200px', margin: '0.5rem 0' }}>
            <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.3))' }}></div>
            <span style={{ fontSize: '1rem', fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--gold)', letterSpacing: '0.15em' }}>OR</span>
            <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(201,168,76,0.3), transparent)' }}></div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap', maxWidth: '900px', padding: '0 1rem' }}>
            <button className="gn-cta-btn" onClick={scrollToPrivateExperience}>Book a Private Mystery</button>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', fontFamily: 'var(--font-body)', lineHeight: '1.6', margin: 0, maxWidth: '400px', textAlign: 'center' }}>
              Reserve the entire experience for your group on a date of your choice (subject to availability).
            </p>
          </div>
        </div>

      </section>

      {/* Where You'll Be */}
      <LocationMap location={location} />

      {/* Show Dates */}
      <section className="gn-dates-section" ref={datesSectionRef}>
        <div className="gn-dates-header"><h2>Select an Event Date</h2><p className="gn-dates-hint">To enquire about dates, select the card below to check directly with the organiser on WhatsApp.</p></div>
        <div className="gn-dates-grid">
          <div
            className="gn-announce-card is-enquirable"
            role="button"
            tabIndex={0}
            aria-label="Enquire about upcoming Greater Noida mystery dates"
            onClick={() => setEnquiryOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setEnquiryOpen(true);
              }
            }}
          >
            <span className="gn-announce-eyebrow">Coming Soon</span>
            <h3 className="gn-announce-title">Dates for Nov&ndash;Dec to be announced soon</h3>
            <div className="gn-announce-divider"><span></span><i>✦</i><span></span></div>
          </div>
        </div>
        {/* WhatsApp Contact */}
        <div style={{ position: 'relative', zIndex: 1, padding: '3rem 2rem', maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '1.2rem', color: '#E8E3E3', marginBottom: '1.5rem', lineHeight: '1.8' }}>
            To book your experience for any date, contact us now on WhatsApp
          </p>
          <a href="https://wa.me/918279845322?text=Hi%20Mystery%20Mansion,%20I%20would%20like%20to%20book%20an%20experience%20at%20Greater%20Noida" target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.8rem', padding: '12px 28px', backgroundColor: '#25D366', color: '#fff', textDecoration: 'none', borderRadius: '8px', fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1rem', transition: 'all 0.3s ease' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.064 3.488z" /></svg>
            +91 8279845322
          </a>
        </div>
      </section>

      {/* Private Experience */}
      <section className="gn-dates-section" ref={privateExperienceSectionRef}>
        <div className="gn-dates-header"><h2>Book a Private Mystery</h2></div>
        <div style={{ position: 'relative', zIndex: 1, padding: '4rem 2rem', maxWidth: '1300px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <div style={{ fontFamily: 'Avenir, sans-serif', fontSize: '1.5rem', fontWeight: 600, color: '#D4AF37', letterSpacing: '0.15em', marginBottom: '1rem', textTransform: 'uppercase' }}>
                Exclusive Group Experience
              </div>
              <div style={{ fontFamily: 'Avenir, sans-serif', fontSize: '1.1rem', lineHeight: '1.8', color: '#E8E3E3', maxWidth: '450px' }}>
                <p>Reserve the entire mansion for a private night of mystery and intrigue. With the complete experience dedicated to your group, you and your guests will step into an unfolding story filled with hidden clues, unexpected twists, and secrets waiting to be discovered.</p>
                <p>Choose your preferred date, gather your team of investigators, and work together to unravel the mystery before the night is over.</p>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div style={{ position: 'relative', width: '100%', maxWidth: '450px', overflow: 'hidden', borderRadius: '16px', boxShadow: '0 16px 48px rgba(0,0,0,0.5)', transition: 'all 0.4s ease', cursor: 'pointer' }}>
                <img src={noidaImg} alt="Private mystery mansion experience" style={{ width: '100%', height: 'auto', display: 'block', aspectRatio: '4/3', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)', padding: '2rem 1.5rem 1.5rem', color: '#fff', fontFamily: 'Avenir, sans-serif', fontSize: '0.95rem', lineHeight: '1.6' }}>
                  Step into mystery. Uncover secrets. Create memories.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
